// Interactive Insights: step crash-by-crash through every CORRELATED crash
// (a crash with >=1 RDV-identified deficiency at its location, under the
// current filters), with a plain-language narrative per crash and an impact
// summary (potentially avoidable / severity-reducible crashes + FHWA
// comprehensive-cost figure). Navigating flies the map to the crash.
import React, { useEffect, useMemo, useState } from 'react';
import {
  computeImpactSummary, crashSentence, findingSentence, fmtUsd,
  lrsLine, visibleFindings,
} from './insightsText';

// "Go to Report": one relevant report -> direct button; several -> chooser.
// URLs are the analysis docs' own UrlReport deep links (they anchor the online
// report at the deficiency's mile point), or the derived hcurves /
// guardrail_warrant dashboard pages.
function GoToReport({ findings }) {
  const [pick, setPick] = useState('');
  const reports = findings
    .filter(([, f]) => f.reportUrl)
    .map(([a, f]) => [a, f.reportUrl]);
  if (!reports.length) return null;
  if (reports.length === 1) {
    return (
      <a className="safety-btn safety-report-btn" href={reports[0][1]}
        target="_blank" rel="noreferrer">
        Go to {reports[0][0]} Report ↗
      </a>
    );
  }
  return (
    <span className="safety-report-choose">
      <select value={pick} onChange={(e) => setPick(e.target.value)}>
        <option value="">Choose report…</option>
        {reports.map(([a, url]) => <option key={a} value={url}>{a} report</option>)}
      </select>
      <a className={`safety-btn safety-report-btn ${pick ? '' : 'disabled'}`}
        href={pick || undefined} target="_blank" rel="noreferrer"
        onClick={(e) => { if (!pick) e.preventDefault(); }}>
        Go to Report ↗
      </a>
    </span>
  );
}

export default function InsightsPanel({ crashes, road, analysis, onFocus, costTable }) {
  const correlated = useMemo(
    () => (crashes || [])
      .filter((c) => visibleFindings(c, analysis).length > 0)
      .sort((a, b) => a.milepost - b.milepost),
    [crashes, analysis]
  );
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [analysis, correlated.length]);

  const summary = useMemo(() => computeImpactSummary(correlated, costTable), [correlated, costTable]);

  if (!correlated.length) {
    return (
      <div className="safety-muted">
        No correlated crashes in the current selection — no crash has a
        {analysis === 'ALL' ? 'n RDV-identified' : ` ${analysis}`} deficiency at its location.
      </div>
    );
  }

  const i = Math.min(idx, correlated.length - 1);
  const crash = correlated[i];
  const findings = visibleFindings(crash, analysis);

  const go = (next) => {
    const j = (i + next + correlated.length) % correlated.length;
    setIdx(j);
    const c = correlated[j];
    if (onFocus) onFocus({ lat: c.lat, lng: c.lon, zoom: 15, key: `insight-${c.id}-${Date.now()}` });
  };

  return (
    <div className="safety-insights">
      <div className="safety-insights-nav">
        <button type="button" className="safety-btn" onClick={() => go(-1)}>‹ Prev</button>
        <span className="safety-insights-counter">
          Correlation {i + 1} of {correlated.length}
        </span>
        <button type="button" className="safety-btn" onClick={() => go(1)}>Next ›</button>
        <button type="button" className="safety-btn safety-btn-ghost"
          onClick={() => onFocus && onFocus({ lat: crash.lat, lng: crash.lon, zoom: 15, key: `insight-${crash.id}-${Date.now()}` })}>
          Show on map
        </button>
      </div>

      <div className="safety-insight-card">
        <div className="safety-insight-lrs">{lrsLine(crash, road)}
          <span className="safety-insight-date">
            {crash.datetime ? ` · ${crash.datetime.slice(0, 16).replace('T', ' ')}` : ''}
          </span>
        </div>
        <p className="safety-insight-crash">{crashSentence(crash)}</p>
        <ul className="safety-insight-findings">
          {findings.map(([a, f]) => (
            <li key={a}><strong>{a}</strong> — {findingSentence(a, f, crash)}</li>
          ))}
        </ul>
        {crash.isAnimal && (
          <p className="safety-muted">Animal strike — sight-distance correlation is
            case-dependent; excluded from the cost figures below.</p>
        )}
        <div className="safety-insight-actions">
          <GoToReport findings={findings} />
        </div>
      </div>

      <div className="safety-insight-summary">
        <p>
          <strong>{summary.n}</strong> non-animal crash{summary.n === 1 ? '' : 'es'} in this
          selection occurred at a location with at least one RDV-identified deficiency
          ({Object.entries(summary.byKabco).map(([k, n]) => `${n} ${k}`).join(', ')} by
          KABCO maximum injury). Had the corresponding RDV analysis recommendations been
          implemented, these crashes are candidates for <em>prevention or reduced injury
          severity</em>.
        </p>
        {summary.mitigated > 0 && (
          <p>
            In <strong>{summary.mitigated}</strong> of them, a guardrail already in place at an
            RDV-warranted location was struck — likely reducing severity. These crashes
            <em> validate</em> the GRW warrant rather than argue for a new installation.
          </p>
        )}
        <p>
          Estimated comprehensive societal cost of these crashes:&nbsp;
          <strong>{fmtUsd(summary.cost)}</strong>
          {summary.lives > 0 && (
            <> — including <strong>{summary.lives} li{summary.lives === 1 ? 'fe' : 'ves'}</strong> that
              might have been saved</>
          )}.
        </p>
        {summary.animalSsd > 0 && (
          <p>
            Additionally, <strong>{summary.animalSsd}</strong> animal
            strike{summary.animalSsd === 1 ? '' : 's'} occurred at SSD-deficient
            locations — limited stopping sight distance may have contributed
            (case-dependent; not included in the figures above).
          </p>
        )}
        <p className="safety-muted safety-insight-source">
          Cost basis: {summary.source}. Correlation is spatial/mechanistic coincidence with a
          deficiency, not established causation; treat as candidates for engineering review.
        </p>
      </div>
    </div>
  );
}
